import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NonIdealState, Spinner } from '@blueprintjs/core';
import { useGlobalMetadata } from '../../utils/providers';
import { useMyCapabilities } from '../../utils/metadata/capabilities';
import { useGridLayout } from '../../hooks/useGridLayout';
import { queryClient } from '../../utils/constants';
import { requestReportGridRefresh } from '../../utils/reportGridRefresh';
import { REPORT_ROWS_QUERY_KEY } from '../ReportPage/reportGridDataSource';
import { useEditUndoStack } from '../WorkspaceGrid/useEditUndoStack';
import {
  ReportGrid,
  decodeGridState,
  decodeSortState,
  encodeGridState,
  encodeSortState,
  type ReportCellEdit,
  type ReportSortModelItem,
} from '../ReportPage/ReportGrid';
import { useReportRecordInteractions } from '../ReportPage/useReportRecordInteractions';
import { ALL_TIME_PRESET, DateRangeSelect, PAST_30_DAYS_PRESET } from '../ui/DateRangeSelect';
import { useIsInstantlyPartner } from '../../hooks/useIsInstantlyPartner';
import { SubmissionBulkEdit, type BulkFieldKey } from './SubmissionBulkEdit';
import {
  SUBMISSIONS_REPORT_KEY,
  SUBMISSION_LIST_DEFAULT_SORT,
  SUBMISSION_STATUS_OPTIONS,
  submissionListLayoutKey,
  submissionListBaseFilterModel,
  submissionSearchFilterModel,
} from './submissionListState';
import './SubmissionPage.css';

const FILTERS_QUERY_PARAM = 'filters';
const SORT_QUERY_PARAM = 'sort';

interface SubmissionListGridProps {
  /**
   * The report the grid reads: 'submissions' (the list, public.rpt_submissions_v)
   * or a workflow submenu's ('awaiting_documents', 'unresponsive_submissions').
   */
  reportKey?: string;
  /** The submenu's badge next to the title (workflowReports.ts). */
  contextLabel?: string;
}

/**
 * REG-828: the Submissions list (/submission, phase 3) and its workflow
 * submenus on the report grid's server data path. Every filter, sort and the
 * count run in the database over every submission the user can see, not over
 * the rows loaded so far. Default columns and their order are the report's
 * (Admin > Reports, or "Save as default layout"); each user's layout is saved
 * under 'report_<key>', like any report page. The status picker is the list's
 * own; each submenu's report already holds one status group.
 *
 * Undo (REG-739) reverses the page's inline and bulk edits, newest first, as
 * the list did before it moved to the report grid (useEditUndoStack).
 */
export default function SubmissionListGrid({
  reportKey = SUBMISSIONS_REPORT_KEY,
  contextLabel,
}: SubmissionListGridProps = {}): React.ReactElement {
  const { data: metadata } = useGlobalMetadata();
  const { hasCapability, isLoading: capabilitiesLoading } = useMyCapabilities();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [datePreset, setDatePreset] = useState(PAST_30_DAYS_PRESET);
  // The Instantly partner (REG-922) has a few deals: open on all dates.
  const isInstantlyPartner = useIsInstantlyPartner();
  useEffect(() => {
    if (isInstantlyPartner) setDatePreset(ALL_TIME_PRESET);
  }, [isInstantlyPartner]);
  const [selectedRows, setSelectedRows] = useState<Record<string, any>[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const report = metadata?.reportData?.byKey?.[reportKey];
  const isList = reportKey === SUBMISSIONS_REPORT_KEY;
  const recordInteractions = useReportRecordInteractions(report, metadata ?? undefined);
  const canBulkEdit = !capabilitiesLoading && hasCapability('edit_deals');
  const {
    loaded: layoutLoaded,
    initialColumnState: savedLayout,
    initialDefaults: savedLayoutDefaults,
    save: saveLayout,
    clear: clearLayout,
  } = useGridLayout(submissionListLayoutKey(reportKey));
  const handleColumnStateChange = useCallback((columnState: Parameters<typeof saveLayout>[0], defaults?: Parameters<typeof saveLayout>[1]) => {
    // The grid reports an empty state when its columns are reset.
    if (columnState.length === 0) clearLayout();
    else saveLayout(columnState, defaults);
  }, [clearLayout, saveLayout]);

  const baseFilterModel = useMemo(
    () => submissionListBaseFilterModel(selectedStatus, datePreset),
    [datePreset, selectedStatus],
  );
  const filterModelFromUrl = useMemo<Record<string, any>>(
    () => decodeGridState<Record<string, any>>(searchParams.get(FILTERS_QUERY_PARAM), {}),
    [searchParams],
  );
  const sortModelFromUrl = useMemo<ReportSortModelItem[]>(
    () => decodeSortState(searchParams.get(SORT_QUERY_PARAM)),
    [searchParams],
  );
  const effectiveSortModel = sortModelFromUrl.length > 0 ? sortModelFromUrl : SUBMISSION_LIST_DEFAULT_SORT;

  // Functional URL updates: a record panel opened from the grid adds its own
  // params moments earlier (see ReportPage).
  const updateUrlParam = useCallback((paramName: string, encoded: string | null) => {
    setSearchParams((prev) => {
      if ((encoded ?? null) === prev.get(paramName)) return prev;
      const next = new URLSearchParams(prev);
      if (encoded) next.set(paramName, encoded);
      else next.delete(paramName);
      return next;
    });
  }, [setSearchParams]);

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
    setSelectionResetKey((key) => key + 1);
  }, []);
  const selectedIds = useMemo(
    () => selectedRows.map((row) => Number(row.id)).filter((id) => Number.isFinite(id)),
    [selectedRows],
  );
  // Undo: the reverted rows are re-read in place, like after an edit.
  const refetchRows = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [REPORT_ROWS_QUERY_KEY] });
    requestReportGridRefresh();
  }, []);
  const undo = useEditUndoStack({ onUndone: refetchRows });
  const submissionSchema = metadata?.tableData?.byName?.submission?.schema_name ?? 'public';
  const handleCellEditSaved = useCallback((edit: ReportCellEdit) => undo.push({
    tableName: edit.tableName,
    schema: edit.schema,
    field: edit.field,
    newValue: edit.newValue,
    label: edit.label,
    dataType: edit.dataType,
    changes: [{ recordId: edit.recordId, oldValue: edit.oldValue }],
  }), [undo]);
  const undoControl = useMemo(() => ({
    canUndo: undo.canUndo,
    nextUndoLabel: undo.nextUndoLabel,
    onUndo: () => { void undo.undoLast(); },
  }), [undo]);

  // The written rows are re-read in place; a status change can move them out
  // of the current status filter, which the refetch reflects.
  const handleBulkSaved = useCallback((field: BulkFieldKey, value: string | number | null, ids: Set<number>, previous: Map<number, unknown>) => {
    const fieldMeta = (metadata as { fieldData?: Record<string, { byFieldName?: Record<string, { ui_label?: string | null; data_type?: string | null }> }> } | null)
      ?.fieldData?.submission?.byFieldName?.[field];
    undo.push({
      tableName: 'submission',
      schema: submissionSchema,
      field,
      newValue: value,
      label: fieldMeta?.ui_label || field,
      dataType: fieldMeta?.data_type,
      changes: [...ids].map((id) => ({ recordId: id, oldValue: previous.has(id) ? previous.get(id) : null })),
    });
    requestReportGridRefresh();
  }, [metadata, submissionSchema, undo]);

  if (!metadata || !layoutLoaded) {
    return <NonIdealState icon={<Spinner />} title="Loading submissions..." />;
  }
  if (!report) {
    return (
      <NonIdealState
        icon="search"
        title="Submissions list not available"
        description={`The "${reportKey}" report is not registered on this database.`}
      />
    );
  }

  return (
    <section className="submission-page submission-page--report">
      <ReportGrid
        report={report}
        metadata={metadata}
        className="submission-report-grid"
        showDescription={false}
        showViewRecord={false}
        contextBadge={contextLabel}
        initialColumnState={savedLayout ?? undefined}
        initialColumnDefaults={savedLayoutDefaults}
        onColumnStateChange={handleColumnStateChange}
        baseFilterModel={baseFilterModel}
        searchFilterModel={submissionSearchFilterModel}
        searchPlaceholder="Search submissions..."
        noRowsMessage="No submissions match."
        rowHeight={40}
        navigateToRecord={recordInteractions.navigateToRecord}
        onRowClick={recordInteractions.onRowClick}
        prefetchRowRecord={recordInteractions.prefetchRowRecord}
        rowActions={recordInteractions.rowActions}
        rowSelection={canBulkEdit ? 'multiple' : undefined}
        onSelectionChanged={canBulkEdit ? setSelectedRows : undefined}
        selectionResetKey={selectionResetKey}
        urlState={{
          filterModel: filterModelFromUrl,
          sortModel: effectiveSortModel,
          onFilterModelChange: (filterModel) => updateUrlParam(FILTERS_QUERY_PARAM, encodeGridState(filterModel)),
          onSortModelChange: (sortModel) => updateUrlParam(SORT_QUERY_PARAM, encodeSortState(sortModel)),
        }}
        resetSortModel={SUBMISSION_LIST_DEFAULT_SORT}
        onCellEditSaved={handleCellEditSaved}
        undo={undoControl}
        headerActions={(
          <>
            {isList && (
              <select
                className="submission-status-select"
                aria-label="Submission status"
                value={selectedStatus}
                onChange={(event) => setSelectedStatus(event.target.value)}
              >
                {SUBMISSION_STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            )}
            <DateRangeSelect className="sky-report-date-range" value={datePreset} onChange={setDatePreset} />
            {canBulkEdit && (
              <SubmissionBulkEdit selectedIds={selectedIds} onClearSelection={clearSelection} onSaved={handleBulkSaved} undoable />
            )}
          </>
        )}
      />
    </section>
  );
}
